import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/authContext';
import { useOrgFeatureState } from '../lib/orgFeatures';
import { api, throwIfNotOk } from '../lib/api';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import type { OrgAddress as Address } from '@siliconx/shared';

export const Route = createFileRoute('/locations')({
  component: LocationsPage,
});

// Standard IANA time-zone names straight from the browser — no dependency, no
// hand-maintained list. Falls back to empty on runtimes without the API.
const TIME_ZONES: string[] =
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];

// Country names derived from the browser's own ISO 3166 data: walk every
// alpha-2 code and keep the ones Intl.DisplayNames resolves to a real name.
// No dependency, no hand-maintained country array. Empty if the API is absent.
const COUNTRIES: string[] = (() => {
  if (typeof Intl.DisplayNames !== 'function') return [];
  const names = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });
  const out = new Set<string>();
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b);
      const name = names.of(code);
      if (name && name !== code) out.add(name);
    }
  }
  return [...out].sort();
})();

interface Location {
  id: string;
  code: string;
  name: string;
  regionId: string;
  address: Address | null;
  timeZone: string | null;
  isActive: boolean;
  createdAt: string | null;
}

interface RegionOption {
  id: string;
  name: string;
}

function Toast({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div className={cn('fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg', type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white')}>
      {message}
    </div>
  );
}

function LocationDialog({
  open,
  regions,
  existing,
  onClose,
  onSaved,
}: {
  open: boolean;
  regions: RegionOption[];
  existing: Location | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [regionId, setRegionId] = useState('');
  const [addr, setAddr] = useState<NonNullable<Address>>({});
  const [timeZone, setTimeZone] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setCode(existing?.code ?? '');
    setName(existing?.name ?? '');
    setRegionId(existing?.regionId ?? '');
    setAddr(existing?.address ?? {});
    setTimeZone(existing?.timeZone ?? '');
    setIsActive(existing?.isActive ?? true);
  }, [open, existing]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!regionId) { setErr('Region is required'); return; }
    setSubmitting(true);
    const address = Object.values(addr).some((v) => v && v.trim()) ? addr : null;
    try {
      if (existing) {
        const res = await api.api.client.locations[':id'].$patch({
          param: { id: existing.id },
          json: { code, name, regionId, address, timeZone: timeZone || null, isActive },
        });
        await throwIfNotOk(res);
        onSaved('Location updated');
      } else {
        const res = await api.api.client.locations.$post({
          json: { code, name, regionId, address, timeZone: timeZone || null },
        });
        await throwIfNotOk(res);
        onSaved('Location created');
      }
      onClose();
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;
  const field = 'w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
  const lbl = 'block text-xs font-medium text-zinc-500 mb-1';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-base font-semibold text-zinc-900">{existing ? 'Edit Location' : 'Add Location'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Code</label>
              <input required value={code} onChange={(e) => setCode(e.target.value)} className={field} placeholder="DC-1" />
            </div>
            <div>
              <label className={lbl}>Region</label>
              <select required value={regionId} onChange={(e) => setRegionId(e.target.value)} className={field}>
                <option value="">Select a region…</option>
                {regions.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
              </select>
            </div>
          </div>
          <div>
            <label className={lbl}>Name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} className={field} placeholder="Primary Data Center" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Street</label>
              <input value={addr.street ?? ''} onChange={(e) => setAddr({ ...addr, street: e.target.value })} className={field} />
            </div>
            <div>
              <label className={lbl}>City</label>
              <input value={addr.city ?? ''} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={field} />
            </div>
            <div>
              <label className={lbl}>State</label>
              <input value={addr.state ?? ''} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className={field} />
            </div>
            <div>
              <label className={lbl}>Postal</label>
              <input value={addr.postal ?? ''} onChange={(e) => setAddr({ ...addr, postal: e.target.value })} className={field} />
            </div>
            <div>
              <label className={lbl}>Country</label>
              {/* Type-to-filter combobox: suggestions are the enumeration, free
                  text still allowed (preserves legacy values). */}
              <input list="country-list" value={addr.country ?? ''} onChange={(e) => setAddr({ ...addr, country: e.target.value })} className={field} placeholder="Start typing…" />
            </div>
            <div>
              <label className={lbl}>Time zone</label>
              <input list="tz-list" value={timeZone} onChange={(e) => setTimeZone(e.target.value)} className={field} placeholder="Start typing…" />
            </div>
          </div>
          <datalist id="country-list">
            {COUNTRIES.map((c) => (<option key={c} value={c} />))}
          </datalist>
          <datalist id="tz-list">
            {TIME_ZONES.map((tz) => (<option key={tz} value={tz} />))}
          </datalist>
          {existing && (
            <label className="flex items-center gap-2 text-sm text-zinc-700">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
            </label>
          )}
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100">Cancel</button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// The `address` jsonb column's Kysely-codegen type is a deeply recursive
// JSON value, which blows up TS's instantiation depth when Hono's RPC client
// tries to infer the full response shape here — cast explicitly instead.
async function fetchLocations(): Promise<Location[]> {
  const res = await api.api.client.locations.$get();
  await throwIfNotOk(res);
  return (await res.json()) as unknown as Location[];
}

async function fetchRegions(): Promise<RegionOption[]> {
  const res = await api.api.client.locations.regions.$get();
  await throwIfNotOk(res);
  return res.json();
}

function LocationsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const locationsFeature = useOrgFeatureState('locations');
  const canLocations = locationsFeature === 'enabled';
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Admin-only page (mirrors team.tsx) — non-admins bounce to profile.
  // RUF-550: locations management is portfolio self-service, not
  // completeness-gated.
  //
  // SIL-1054/SIL-1060: /locations is an orgFeature: 'locations'-gated
  // experience (customerNav.ts hides the nav entry entirely for an org type
  // that doesn't unlock it, and the API rejects locations operations against
  // one, clientAccess.ts's access({ orgFeature: 'locations' })) — this is the
  // same reachable-by-URL backstop the root-level `OrgFeatureGate`
  // (__root.tsx) already provides. SIL-1060: target must match
  // OrgFeatureGate's own destination (/dashboard) — Locations has no
  // `fallbackTo` (unlike Settings/orgProfile), so there is no substitute page
  // to remap to; this used to send an individual org to /individual-profile
  // instead (SIL-1054's original fix, copied before SIL-1055 corrected the
  // identical bug on team.tsx), which raced OrgFeatureGate's /dashboard
  // redirect and (as the later-mounted effect, Locations' page sits inside
  // AppShell's Outlet, after OrgFeatureGate in __root.tsx's render order)
  // always won. Waits for 'pending' to resolve (does not fire while identity
  // is still loading), same as OrgFeatureGate.
  useEffect(() => {
    if (authLoading) return;
    if (locationsFeature === 'pending') return;
    if (locationsFeature === 'disabled') {
      void navigate({ to: '/dashboard', replace: true });
      return;
    }
    if (user?.role !== 'admin') {
      void navigate({ to: '/profile', replace: true });
    }
  }, [authLoading, user, locationsFeature, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([fetchLocations(), fetchRegions()])
      .then(([locs, regs]) => { setLocations(locs); setRegions(regs); })
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // SIL-1054: skip the fetch entirely for an org that doesn't unlock
    // 'locations' — the redirect effect above sends it away, and
    // /api/client/locations + /api/client/locations/regions 403 for one
    // anyway (access({ orgFeature: 'locations' })).
    if (!authLoading && user?.role === 'admin' && canLocations) load();
  }, [authLoading, user, canLocations, load]);

  if (authLoading || locationsFeature === 'pending') return <LoadingShell rows={4} />;
  if (user?.role !== 'admin' || !canLocations) return null;

  const regionName = (id: string) => regions.find((r) => r.id === id)?.name ?? id;

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <PageHeader title="Locations" pinTo="/locations" />
          <Button size="sm" className="text-sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            Add Location
          </Button>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500">Loading locations…</div>
          ) : error ? (
            <div className="p-6 text-sm text-red-600">Error: {error}</div>
          ) : locations.length === 0 ? (
            <div className="p-6 text-sm text-zinc-400">No locations yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-medium text-zinc-500">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Region</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {locations.map((l) => (
                  <tr key={l.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium text-zinc-800">{l.code}</td>
                    <td className="px-4 py-3 text-zinc-700">{l.name}</td>
                    <td className="px-4 py-3 text-zinc-500">{regionName(l.regionId)}</td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', l.isActive ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-500')}>
                        {l.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => { setEditing(l); setDialogOpen(true); }} className="rounded-md px-3 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <LocationDialog
        open={dialogOpen}
        regions={regions}
        existing={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={(msg) => { setToast({ message: msg, type: 'success' }); load(); }}
      />

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </>
  );
}
